import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  useProvincesQuery,
  useToggleProvinceEnabled,
  useZonesQuery,
} from "../api/queries";
import { ProvincesTour, useProvincesTour } from "../tour";
import { ProvincesFilter } from "../ui/ProvincesFilter";
import { ProvincesTable } from "../ui/ProvincesTable";
import { ProvincesToolbar } from "../ui/ProvincesToolbar";

export function ProvincesPage() {
  const nav = useNavigate({ from: "/app/geo/provinces" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/geo/provinces" });
  const list = useProvincesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const zonesQuery = useZonesQuery();
  const zones = zonesQuery.data ?? [];
  const canManage = useActionPermission(["geo:create"]);
  const toggleEnabled = useToggleProvinceEnabled();
  const { run, handleJoyrideCallback, startTour } = useProvincesTour();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ແຂວງ" },
          ]}
        />
      </Header>
      <Main>
        <ProvincesToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/geo/provinces/new" })}
          onStartTour={startTour}
        />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ProvincesFilter zones={zones} />
          <ProvincesTable
            canManage={!!canManage}
            zones={zones}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(row) =>
              nav({
                to: "/app/geo/provinces/$id/edit",
                params: { id: row.id },
              })
            }
            onToggleEnabled={(id) =>
              toast.promise(toggleEnabled.mutateAsync(id), {
                loading: "ກຳລັງອັບເດດ...",
                success: "ອັບເດດສຳເລັດ",
                error: "ອັບເດດລົ້ມເຫຼວ",
              })
            }
          />
        </div>
        <ProvincesTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
